import type { ComponentType } from "react";
import {
  RocketIcon,
  LinkIcon,
  CubeIcon,
  ShieldCheckIcon,
  LockIcon,
  WorkspaceIcon,
  RuntimeIcon,
  DeploymentIcon,
  GitIcon,
  TerminalIcon,
  AiIcon,
  ExtensionIcon,
  IdentityIcon,
  CloudIcon,
  type KeycapIconName,
  type KeycapIconProps,
} from "./keycapIcons";

/**
 * Every concept the site represents visually — the four homepage insights
 * plus every capability shown on /product and every dedicated page's hero.
 * One catalogue, one component (`Keycap`), no other tile system anywhere on
 * the site. See the 2026-07-17 keycap art-direction pass.
 */
export type KeycapId =
  | "compose"
  | "connect"
  | "qualify"
  | "publish"
  | "enterprise"
  | "workspace"
  | "runtime"
  | "deployment"
  | "git"
  | "terminal"
  | "ai"
  | "extension"
  | "identity"
  | "cloud";

interface KeycapDefinition {
  icon: KeycapIconName;
  /** The key's resting RGB-style underglow and legend colour, always a
   * `var(--devory-*)` reference — never a literal hex — so the whole tile
   * system stays inside the one approved pink-to-orange energy family (plus
   * a reserved success green for "qualified"). No violet, no blue, no cyan:
   * a curated seven-colour palette reads as one keyset; fourteen distinct
   * hues would read as noise. See the 2026-07-18 brand redesign pass. */
  accent: string;
}

export const keycapCatalog: Record<KeycapId, KeycapDefinition> = {
  compose: { icon: "cube", accent: "var(--devory-pink)" },
  git: { icon: "git", accent: "var(--devory-pink)" },
  connect: { icon: "link", accent: "var(--devory-hot-pink)" },
  ai: { icon: "ai", accent: "var(--devory-hot-pink)" },
  runtime: { icon: "runtime", accent: "var(--devory-coral)" },
  identity: { icon: "identity", accent: "var(--devory-coral)" },
  enterprise: { icon: "lock", accent: "var(--devory-coral)" },
  publish: { icon: "rocket", accent: "var(--devory-orange)" },
  deployment: { icon: "deployment", accent: "var(--devory-orange)" },
  workspace: { icon: "workspace", accent: "var(--devory-warm-orange)" },
  extension: { icon: "extension", accent: "var(--devory-warm-orange)" },
  terminal: { icon: "terminal", accent: "var(--devory-amber)" },
  cloud: { icon: "cloud", accent: "var(--devory-amber)" },
  qualify: { icon: "shield-check", accent: "var(--devory-success)" },
};

export const keycapIconComponents: Record<KeycapIconName, ComponentType<KeycapIconProps>> = {
  rocket: RocketIcon,
  link: LinkIcon,
  cube: CubeIcon,
  "shield-check": ShieldCheckIcon,
  lock: LockIcon,
  workspace: WorkspaceIcon,
  runtime: RuntimeIcon,
  deployment: DeploymentIcon,
  git: GitIcon,
  terminal: TerminalIcon,
  ai: AiIcon,
  extension: ExtensionIcon,
  identity: IdentityIcon,
  cloud: CloudIcon,
};
